import { AnyObject, Id, PartialRequired } from '@vimeo/core/types/generic';

import { GctlConfig } from './dreamstate_gctl_scope';
import {
  IApiCallOptions,
  IApiCallParameters,
  sendApiRequest,
  TApiMethodParameters,
} from './vimeo_base';
import { ERequestMethod, IVimeoApiConnection } from '../../types/api';
import { ELiveErrorCode } from '../../types/error';
import { LiveError } from '../../utils/error';

/**
 * Generic type used with сщттусешщт api methods, allows skipping repeated fields declaration.
 */
export type TConnectionMethodParameters = {
  connection: IVimeoApiConnection;
  gctlConfig: GctlConfig;
  fields?: Array<string>;
  standardize?: boolean;
} & Omit<IApiCallParameters, 'path'>;

export type TConnectionSupportedMethodParams<T> =
  | TConnectionMethodParameters
  | TApiMethodParameters<T>;

/**
 * Check if params schema is containing connection config.
 */
export function areConnectionParams(params: AnyObject): params is TConnectionMethodParameters {
  return Boolean(params['connection']);
}

/**
 * Specify id for connection if it contains generic endpoint not bound to other places.
 */
export function specifyConnectionId<T extends IVimeoApiConnection>(connection: T, id: Id): T {
  return { ...connection, uri: `${connection.uri}/${id}` };
}

/**
 * Send generic request based on connection object.
 * Allows to create fully backend controlled endpoints.
 */
export function sendConnectionRequest<T>(
  {
    connection,
    method = ERequestMethod.GET,
    body,
    query,
    fields = [],
    gctlConfig,
    standardize,
  }: TConnectionMethodParameters,
  options: IApiCallOptions = {},
): Promise<T> {
  if (!connection?.options?.includes(method)) {
    throw new LiveError(
      `Unexpected error: connection '${connection.uri}' does not expect method '${method}'.`,
      { code: ELiveErrorCode.API_ERROR, data: connection },
    );
  }

  return sendApiRequest(
    {
      method,
      path: connection.uri,
      body,
      query,
      gctlConfig,
      fields,
      standardize,
    },
    options,
  );
}

/**
 * Single wrapper to include connection support and prevent duplicated logic for every method.
 */
export function withConnectionSupport<T, D>(
  method: ERequestMethod,
  methodParams: TConnectionMethodParameters | TApiMethodParameters<D>,
  apiParamsFactory: (
    params: TApiMethodParameters<D>,
  ) => PartialRequired<IApiCallParameters, 'path'>,
  {
    body,
    query,
    additionalHeaders,
    encodeQuery,
  }: Pick<IApiCallParameters, 'query' | 'body'> & IApiCallOptions = {},
): Promise<T> {
  return areConnectionParams(methodParams)
    ? sendConnectionRequest(
        {
          ...methodParams,
          method,
          body,
          query,
        },
        { additionalHeaders, encodeQuery },
      )
    : sendApiRequest(
        {
          ...methodParams,
          method,
          body,
          query,
          ...apiParamsFactory(methodParams),
        },
        { additionalHeaders, encodeQuery },
      );
}
